import React, { PropsWithChildren } from 'react';
import { Dialog, Divider, IconButton } from '@mui/material';
import { BackChevronIcon, CloseCircleIcon } from '../../ui/icons';

const rootContainer = document.getElementById('root');

type AuthAndCardModalProps = PropsWithChildren & {
    open: boolean;
    onClose: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;
    onCloseClick?: () => void;
    onBack?: () => void;
    isLoading?: boolean;
    donation?: {
        amount: string;
        name: string;
        logoUrl?: string;
    };
};

export const AuthAndCardModal: React.FC<AuthAndCardModalProps> = ({
    open,
    onClose,
    onCloseClick,
    onBack,
    isLoading,
    donation,
    children,
}) => {
    return (
        <Dialog
            id="auth-card-modal"
            open={open}
            onClose={onClose}
            container={rootContainer}
            maxWidth={false}
            sx={{
                '& .MuiBackdrop-root': { backgroundColor: '#f1f0f7cc' },
                '& .MuiDialog-container': {
                    '@media (max-width: 480px)': {
                        alignItems: 'stretch',
                    },
                },
                '& .MuiDialog-paper': {
                    borderRadius: '24px',
                    padding: '48px 40px',
                    backgroundColor: '#ffffff',
                    boxShadow:
                        '0 1px 6px 0 rgba(10, 0, 77, 0.04), 0 5.5px 30px 0 rgba(10, 0, 77, 0.05)',
                    width: 480,
                    maxWidth: 'calc(100vw - 32px)',
                    position: 'relative',
                    overflow: 'visible',
                    '@media (max-width: 480px)': {
                        width: '100%',
                        maxWidth: '100%',
                        height: '100%',
                        maxHeight: '100%',
                        margin: 0,
                        borderRadius: 0,
                        padding: '48px 24px',
                        overflow: 'auto',
                    },
                },
            }}
        >
            {onBack ? (
                <IconButton
                    id="auth-card-modal-back-button"
                    className="absolute left-3 top-3"
                    onClick={onBack}
                    disabled={isLoading}
                    data-testid="auth-modal-back"
                >
                    <BackChevronIcon />
                </IconButton>
            ) : null}
            <IconButton
                id="auth-card-modal-close-button"
                className="absolute right-3 top-3"
                onClick={onCloseClick}
                disabled={isLoading}
                data-testid="auth-modal-close"
            >
                <CloseCircleIcon />
            </IconButton>
            {donation && donation.logoUrl ? (
                <div
                    id="auth-card-modal-donation-logo-container"
                    className="hidden sm:block absolute w-20 h-20 top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full overflow-hidden"
                >
                    <img
                        id="auth-card-modal-donation-logo"
                        src={donation.logoUrl}
                        alt={donation.name}
                        className="w-full h-full object-cover"
                    />
                </div>
            ) : null}
            {donation ? (
                <>
                    <div
                        id="auth-card-modal-donation-summary"
                        className="mt-6 flex flex-col justify-center mb-8 border-b border-separator"
                    >
                        <div
                            id="auth-card-modal-donation-amount"
                            className="text-center text-heading-medium text-primary"
                        >
                            Give ${donation.amount} to
                        </div>
                        <div
                            id="auth-card-modal-donation-recipient"
                            className="text-center text-heading-small text-secondary"
                        >
                            {donation.name}
                        </div>
                    </div>
                    <Divider id="auth-card-modal-donation-divider" className="" />
                </>
            ) : null}
            {children}
        </Dialog>
    );
};
